/**
 * Message Icon Component
 * Displays message icon with unread badge in header
 */

import React, { useEffect, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { messagingService } from '@/firebase/messagingService';

const MessageIcon: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { organizations } = useOrganization();
  const { hasFeature } = useSubscription();
  const [unreadCount, setUnreadCount] = useState<number>(0);

  const organizationIds: string[] = organizations?.map((o) => o.id).filter((id): id is string => id != null) ?? [];

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setUnreadCount(0);
      return;
    }

    if (organizationIds.length === 0) {
      setUnreadCount(0);
      return;
    }

    const unsubscribe = messagingService.subscribeToUnreadCountAllOrgs(
      user.id,
      organizationIds,
      (count) => {
        setUnreadCount(count);
      }
    );

    messagingService.getUnreadCountAllOrgs(user.id, organizationIds).then((result) => {
      if (result.success && result.data !== undefined) {
        setUnreadCount(result.data);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [isAuthenticated, user?.id, organizationIds.join(',')]);

  const handleClick = () => {
    // Open messages page in new tab
    window.open('/messages', '_blank');
  };

  if (!isAuthenticated || !hasFeature('messaging')) {
    return null;
  }

  return (
    <div className="relative flex items-center">
      <button
        onClick={handleClick}
        className="relative flex items-center justify-center p-2 border border-primary-200 bg-primary-50/30 rounded-md hover:bg-primary-100/50 transition-colors group"
        title="Messages"
      >
        <MessageSquare className="w-5 h-5 text-gray-600 group-hover:text-primary-600 transition-colors" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center min-w-[20px] z-10">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
    </div>
  );
};

export default MessageIcon;
export { MessageIcon };